import { Matrix3, Vector3 } from 'three';

import { isValidNumber } from '../../../utils/math/number';
import { Coordinates } from '../../../viewer/utilities/math-helpers';
import { AvarageError } from './gcp';
import { Marker } from './marker';

type CameraAccuracy = {
  sx: number;
  sy: number;
  sz: number;
  total: number;
};

export class Camera {
  static fromXml(cameraElement: Element): Camera {
    const xml = cameraElement;
    if (!cameraElement.hasAttributes() || !cameraElement.children.length) {
      throw new Error(`Invalid camera element`);
    }

    const referenceElement = Array.from(cameraElement.children).find(
      (e) => e.tagName === 'reference',
    );
    if (!referenceElement?.hasAttributes()) {
      throw new Error(`Camera element missing or contains invalid reference`);
    }

    const transformElement = Array.from(cameraElement.children).find(
      (e) => e.tagName === 'transform',
    );
    const id = Number(cameraElement.getAttribute('id'));
    const label = cameraElement.getAttribute('label') ?? '';
    const enabled = !['false', '0'].includes(cameraElement.getAttribute('enabled') ?? '');
    const sensorId = Number(cameraElement.getAttribute('sensor_id'));
    const referenceEnabled = !['false', '0'].includes(
      referenceElement.getAttribute('enabled') ?? '',
    );

    const position = {
      northing: Number(referenceElement.getAttribute('y')),
      easting: Number(referenceElement.getAttribute('x')),
      height: Number(referenceElement.getAttribute('z')),
    };

    const transform = this.extractTransform(transformElement);
    const locationError = this.getLocationError(cameraElement);
    const referenceError = this.extractCameraAccuracy(referenceElement);

    const camera = new Camera({
      id,
      label,
      enabled,
      sensorId,
      position,
      xml,
      transform,
      referenceEnabled,
      accuracy: referenceError,
    });

    camera.avarageError = locationError;

    return camera;
  }

  private static extractCameraAccuracy(referenceElement: Element): CameraAccuracy | undefined {
    const sx = Number.parseFloat(referenceElement.getAttribute('sx') ?? '');
    const sy = Number.parseFloat(referenceElement.getAttribute('sy') ?? '');
    const sz = Number.parseFloat(referenceElement.getAttribute('sz') ?? '');

    if (isValidNumber(sx) && isValidNumber(sy) && isValidNumber(sz)) {
      return { sx, sy, sz, total: new Vector3(sx, sy, sz).length() };
    }

    return undefined;
  }

  private static getLocationError(cameraElement?: Element): AvarageError | undefined {
    const locationCovariance =
      Array.from(cameraElement?.children ?? []).find((e) => e.tagName === 'location_covariance')
        ?.textContent ?? '';

    const rowMajorArray = locationCovariance.split(' ').map((x) => Number.parseFloat(x));

    if (rowMajorArray.length !== 9 || rowMajorArray.some((x) => !isValidNumber(x))) {
      return undefined;
    }

    const matrix = new Matrix3().fromArray(rowMajorArray).transpose();

    const x = Math.sqrt(matrix.elements[0]);
    const y = Math.sqrt(matrix.elements[4]);
    const z = Math.sqrt(matrix.elements[8]);

    const total = new Vector3(x, y, z).length();

    return { x, y, z, total };
  }

  private static extractTransform(element?: Element): string | undefined {
    const text = element?.textContent?.trim();
    if (text) {
      const numbers = text.split(' ').map(Number);

      // Remove "perspective"
      numbers[3] *= 0;
      numbers[7] *= 0;
      numbers[11] *= 0;

      return numbers.join(',');
    }
  }
  markers: Marker[] = [];
  width = 0;
  height = 0;

  thumbnailUrl?: string;
  previewUrl?: string;
  photoUrl?: string;

  img?: HTMLImageElement;
  finishedLoading = false;
  nearbyCameras: Camera[] = [];
  file?: File;

  avarageError: AvarageError | undefined;
  id: number;
  label: string;
  enabled: boolean;
  referenceEnabled: boolean;
  accuracy?: CameraAccuracy;
  sensorId: number;
  position: Coordinates;
  xml: any;
  transform?: string;

  constructor(properties: {
    id: number;
    label: string;
    enabled: boolean;
    sensorId: number;
    position: Coordinates;
    xml: any;
    transform?: string;
    referenceEnabled: boolean;
    accuracy?: CameraAccuracy;
  }) {
    this.id = properties.id;
    this.label = properties.label;
    this.enabled = properties.enabled;
    this.sensorId = properties.sensorId;
    this.position = properties.position;
    this.xml = properties.xml;
    this.transform = properties.transform;
    this.referenceEnabled = properties.referenceEnabled;
    this.accuracy = properties.accuracy;
  }

  setResolution(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  addMarker(marker: Marker) {
    const existing = this.markers.find((m: any) => m.gcp.name === marker.gcp!.name);
    if (existing) {
      existing.x = marker.x;
      existing.left = marker.left;
      existing.y = marker.y;
      existing.top = marker.top;
      existing.pinned = marker.pinned;
    } else {
      this.markers.push(marker);
    }
  }

  removeMarker(gcpName: string) {
    const index = this.markers.findIndex((m) => m.gcp!.name === gcpName);
    if (index > -1) {
      this.markers.splice(index, 1);
    }
  }
}
